// HotelContactsTable.jsx
// Main table: shows hotel contacts and their email-type subscriptions

import React, { useCallback, useEffect, useMemo, useState } from "react";
import Axios from "axios";
import {
    Box,
    Button,
    Checkbox,
    CircularProgress,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
} from "@mui/material";

import { GBH_ID, SanitizeHotelID, URLAPI } from "../configuration";
import ContactListDrawer from "./ContactListDrawer";
import HoverPaper from "./shared/HoverPaper";
import Icons from "../helpers/icons";
import Colours from "../helpers/colours";
import TableHeadSort from "./TableHeadSort";

const HotelContactsTable = ({ hotelID, auth }) => {
    /* ----------- state ----------- */
    const [contacts, setContacts] = useState([]); // [{ AccountID, EmailAddress, Name }]
    const [subs, setSubs] = useState([]); // [{ AccountID, EmailType }]
    const [emailTypes, setEmailTypes] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const [order, setOrder] = useState("asc");
    const [orderBy, setOrderBy] = useState("bookings");

    const [pending, setPending] = useState({});
    const [saving, setSaving] = useState(false);

    const [modalOpen, setModalOpen] = useState(false);

    const headers = useMemo(
        () => ({ Authorization: `Bearer ${auth.user.jwt}` }),
        [auth],
    );

    const headerStyles = {
        fontSize: "0.9em",
        backgroundColor: Colours.pillarBackgroundAttract,
        textAlign: "left",
        border: "1px solid lightgrey",
    };
    console.log({ emailTypes });
    const tableHeaders = useMemo(() => {
        return [
            { id: "Name", label: "Name", styles: headerStyles },
            {
                id: "ContactEmail",
                label: "Contact email",
                ignore: true,
                styles: headerStyles,
            },
            ...(emailTypes ?? []).map((et) => ({
                id: et,
                label: et === "LastMonthSummaryTable" ? "Monthly emails" : et, // TODO: review this when we handle more than just monthly emails
                ignore: true,
                styles: { ...headerStyles, textAlign: "center" },
            })),
        ];
    }, [emailTypes]);

    /* ----------- fetch ----------- */
    const fetchData = useCallback(() => {
        if (!hotelID) return;
        setLoading(true);
        setError(null);

        Promise.all([
            // NEW: /hotels/contacts
            Axios.get(`${URLAPI}/hotels/contacts`, {
                params: { hotel_id: SanitizeHotelID(hotelID, GBH_ID) },
                headers,
            }),
            Axios.get(`${URLAPI}/hotels/subscriptions`, {
                params: {
                    hotel_id: SanitizeHotelID(hotelID, GBH_ID),
                    only_enabled: true,
                },
                headers,
            }),
        ])
            .then(([resContacts, resSubs]) => {
                setContacts(resContacts.data.data);
                setSubs(resSubs.data.data);

                const extraTypes = resContacts?.data?.email_types; // <- new
                const subsTypes = resSubs?.data?.email_types; // <- keep as fallback
                setEmailTypes(
                    Array.isArray(extraTypes)
                        ? extraTypes
                        : Array.isArray(subsTypes)
                          ? subsTypes
                          : [],
                );

                setPending({});
            })
            .catch((err) => setError(err.message))
            .finally(() => setLoading(false));
    }, [hotelID, headers]);

    useEffect(fetchData, [fetchData]);

    /* ----------- helpers ----------- */
    // const emailTypes = useMemo(
    //     () =>
    //         [...new Set(subs.map((s) => s.EmailType))].sort((a, b) =>
    //             a.localeCompare(b)
    //         ),
    //     [subs]
    // );

    const originalSubs = useMemo(() => {
        const set = new Set();
        subs.forEach((s) => set.add(`${s.AccountID}-${s.EmailType}`));
        return set;
    }, [subs]);

    const isChecked = (id, et) => {
        const key = `${id}-${et}`;
        return key in pending ? pending[key] : originalSubs.has(key);
    };

    const toggle = (id, et, checked) => {
        const key = `${id}-${et}`;
        setPending((prev) => ({ ...prev, [key]: checked }));
    };

    const hasChanges = useMemo(
        () =>
            Object.keys(pending).some(
                (k) => pending[k] !== originalSubs.has(k),
            ),
        [pending, originalSubs],
    );

    const sortedContacts = useMemo(() => {
        // if (orderBy !== "Name") return contacts;
        const sorted = [...contacts];
        sorted.sort((a, b) => {
            const nameA = a.Name?.toLowerCase() ?? "";
            const nameB = b.Name?.toLowerCase() ?? "";
            return order === "asc"
                ? nameA.localeCompare(nameB)
                : nameB.localeCompare(nameA);
        });
        return sorted;
    }, [contacts, order, orderBy]);

    /* ----------- save subs ----------- */
    const handleSaveSubs = async () => {
        if (!hasChanges) return;
        setSaving(true);

        const additions = [];
        const removals = [];

        Object.entries(pending).forEach(([key, value]) => {
            const [acct, et] = key.split("-");
            const wasChecked = originalSubs.has(key);

            if (value && !wasChecked)
                additions.push({ account_id: acct, email_type: et });
            else if (!value && wasChecked)
                removals.push({ account_id: acct, email_type: et });
        });

        try {
            await Promise.all([
                ...additions.map((p) =>
                    Axios.post(
                        `${URLAPI}/hotels/subscriptions`,
                        {
                            hotel_id: SanitizeHotelID(hotelID, GBH_ID),
                            ...p,
                        },
                        { headers },
                    ),
                ),
                ...removals.map((p) =>
                    Axios.delete(`${URLAPI}/hotels/subscriptions`, {
                        params: {
                            hotel_id: SanitizeHotelID(hotelID, GBH_ID),
                            ...p,
                        },
                        headers,
                    }),
                ),
            ]);
            fetchData();
        } catch (err) {
            setError(err.message);
        } finally {
            setSaving(false);
        }
    };

    /* ----------- UI ----------- */
    if (loading)
        return (
            <Box sx={{ display: "flex", justifyContent: "center", my: 4 }}>
                <CircularProgress />
            </Box>
        );

    if (error)
        return (
            <Typography color="error" sx={{ my: 4 }}>
                {error}
            </Typography>
        );

    return (
        <>
            {/* ---- main card ---- */}
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    paddingTop: 2,
                    // paddingBottom: 2,
                    mt: 4,
                }}
            >
                <Typography sx={{}} fontWeight={"bold"} variant="h6">
                    INTERNAL CONTACT LIST
                </Typography>

                <Button
                    variant="text"
                    onClick={() => setModalOpen(true)}
                    startIcon={<Icons.Edit />}
                >
                    Edit Contact List
                </Button>
            </Box>
            <Box component={HoverPaper}>
                {contacts.length === 0 ? (
                    <Typography sx={{ p: 2 }}>
                        No contacts found for this hotel.
                    </Typography>
                ) : (
                    <Table size="small">
                        <TableHeadSort
                            stickyHeads={0}
                            headers={tableHeaders}
                            order={order}
                            orderBy={orderBy}
                            setOrder={setOrder}
                            setOrderBy={setOrderBy}
                        />
                        <TableBody>
                            {sortedContacts.map((c) => (
                                <TableRow key={c.AccountID}>
                                    <TableCell
                                        style={{
                                            border: "1px solid lightgrey",
                                        }}
                                    >
                                        {c.Name}
                                    </TableCell>
                                    <TableCell
                                        style={{
                                            border: "1px solid lightgrey",
                                        }}
                                    >
                                        {c.EmailAddress}
                                    </TableCell>

                                    {(emailTypes ?? []).map((et) => (
                                        <TableCell
                                            style={{
                                                border: "1px solid lightgrey",
                                            }}
                                            key={et}
                                            align="center"
                                        >
                                            <Checkbox
                                                size="small"
                                                checked={isChecked(
                                                    c.AccountID,
                                                    et,
                                                )}
                                                onChange={(e) =>
                                                    toggle(
                                                        c.AccountID,
                                                        et,
                                                        e.target.checked,
                                                    )
                                                }
                                            />
                                        </TableCell>
                                    ))}
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                )}
            </Box>
            {/* ---- save subs button ---- */}
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    p: 2,
                }}
            >
                <Button
                    variant="contained"
                    disabled={!hasChanges || saving}
                    onClick={handleSaveSubs}
                >
                    {saving ? "Saving…" : "Save"}
                </Button>
            </Box>

            {/* ---- modal ---- */}
            <ContactListDrawer
                open={modalOpen}
                onClose={() => setModalOpen(false)}
                hotelID={hotelID}
                auth={auth}
                currentContacts={contacts.map((c) => c.AccountID)}
                onSaved={() => {
                    setModalOpen(false);
                    fetchData();
                }}
            />
        </>
    );
};

export default HotelContactsTable;
